<template>
  <div class="dashboard">
    <div class="main-content">
      <navbar-dashboard name="Compañía" />

      <div class="ew-view">
        <div class="ew-form-page">
          <page-header title="Compañía" subtitle="Datos fiscales y documentación de tu empresa."></page-header>

          <form @submit.prevent="updateCompania">
            <div class="ew-section">
              <h2 class="ew-section-title"><i class="bi bi-building text-primary"></i> Perfil de la empresa</h2>
              <p class="ew-section-subtitle">El tipo de persona y la condición de pago determinan qué documentos necesitamos.</p>
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label for="personType">Tipo de persona</label>
                  <select id="personType" class="custom-select" v-model="company.personType">
                    <option value="Moral">Persona moral</option>
                    <option value="Fisica">Persona física</option>
                  </select>
                </div>
                <div class="form-group col-md-6">
                  <label for="creditType">Condición de pago</label>
                  <select id="creditType" class="custom-select" v-model="company.creditType">
                    <option value="Credito">Con crédito</option>
                    <option value="SinCredito">Sin crédito</option>
                    <option value="PrePago">Prepago</option>
                  </select>
                </div>
                <div class="form-group col-md-6">
                  <label for="internalNumber">Número de cliente</label>
                  <div class="ew-locked">
                    <input id="internalNumber" type="text" class="form-control" :value="company.internalClientNumber || '—'" readonly />
                    <i class="bi bi-lock"></i>
                  </div>
                </div>
                <div class="form-group col-md-6">
                  <label for="internalName">Nombre del cliente</label>
                  <div class="ew-locked">
                    <input id="internalName" type="text" class="form-control" :value="company.internalClientName || '—'" readonly />
                    <i class="bi bi-lock"></i>
                  </div>
                </div>
                <div class="col-12">
                  <small class="ew-hint mt-n2 mb-3"><i class="bi bi-info-circle mr-1"></i>El número y nombre de cliente los asigna SackTech. Si necesitas cambiarlos, contacta a tu ejecutivo.</small>
                </div>
                <div class="form-group col-12 mb-0">
                  <label for="warehouseAddress">Dirección del almacén</label>
                  <input id="warehouseAddress" type="text" class="form-control" v-model="company.defaultWarehouseAddress"
                    placeholder="Calle, número, colonia, ciudad" />
                </div>
              </div>
            </div>

            <div class="ew-section">
              <h2 class="ew-section-title"><i class="bi bi-receipt text-primary"></i> Datos de facturación</h2>
              <p class="ew-section-subtitle">Se usan para emitir tus facturas. Deben coincidir con tu constancia fiscal.</p>
              <div class="form-row">
                <div class="form-group col-md-6" v-for="field in billingFields" :key="field.key">
                  <label :for="'billing-' + field.key">{{ field.label }}</label>
                  <input :id="'billing-' + field.key" :type="field.type || 'text'" class="form-control"
                    v-model="company.billingData[field.key]" :placeholder="field.placeholder || ''"
                    :class="{ 'text-uppercase': field.key === 'rfc' }" />
                </div>
              </div>
            </div>

            <div class="ew-section" v-if="['admin', 'superadmin', 'client', 'admin_add'].includes(role)">
              <h2 class="ew-section-title">
                <i class="bi bi-folder2-open text-primary"></i> Documentación
                <span class="badge ml-1" :class="docsLoaded === currentRequiredDocuments.length ? 'badge-success' : 'badge-warning'">
                  {{ docsLoaded }} de {{ currentRequiredDocuments.length }}
                </span>
              </h2>
              <p class="ew-section-subtitle">Formatos aceptados: PDF, PNG o JPG. Los archivos nuevos se guardan al presionar «Guardar cambios».</p>

              <ul class="ew-docs">
                <li v-for="doc in currentRequiredDocuments" :key="doc.key" class="ew-doc">
                  <span class="ew-doc-icon" :class="{ 'is-done': existingDocs[doc.key] || selectedFiles[doc.key] }">
                    <i class="bi" :class="existingDocs[doc.key] || selectedFiles[doc.key] ? 'bi-check2' : 'bi-file-earmark'"></i>
                  </span>
                  <div class="ew-doc-body">
                    <div class="ew-doc-name">{{ doc.label }}</div>
                    <div class="ew-doc-meta">
                      <template v-if="selectedFiles[doc.key]">
                        <span class="text-primary">Por subir: {{ selectedFiles[doc.key].name }}</span>
                      </template>
                      <template v-else-if="existingDocs[doc.key]">
                        Cargado · <a :href="existingDocs[doc.key]" target="_blank" rel="noopener">Ver documento</a>
                      </template>
                      <template v-else>Pendiente</template>
                    </div>
                  </div>
                  <label class="btn btn-sm btn-outline-secondary mb-0" :for="'file-' + doc.key">
                    <i class="bi bi-upload mr-1"></i> {{ existingDocs[doc.key] ? 'Reemplazar' : 'Subir' }}
                  </label>
                  <input type="file" class="sr-only" :id="'file-' + doc.key"
                    @change="handleFileUpload($event, doc.key)" accept=".pdf,.png,.jpg,.jpeg" />
                </li>
              </ul>
            </div>

            <div class="ew-form-actions">
              <button type="submit" class="btn btn-primary" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm mr-1" role="status"></span>
                {{ loading ? 'Guardando…' : 'Guardar cambios' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import commons from "../commons";
import Navbar from "./Header/Navbar";

export default {
  components: { "navbar-dashboard": Navbar },
  data() {
    return {
      selectedFiles: {},
      existingDocs: {},
      loading: false,
      userId: localStorage.getItem("ew-user-id"),
      documentLists: {
        "Moral-Credito": [
          { key: "constancia_fiscal", label: "Constancia de situación fiscal (del mes en curso)" },
          { key: "comprobante_domicilio", label: "Comprobante de domicilio (más reciente)" },
          { key: "acta_constitutiva", label: "Acta constitutiva" },
          { key: "poder_notarial", label: "Poder notarial" },
          { key: "identificacion_apoderado", label: "Identificación del apoderado (vigente)" },
          { key: "opinion_positiva", label: "Opinión positiva (del mes en curso)" },
          { key: "propuesta_firmada", label: "Propuesta firmada" },
          { key: "solicitud_credito", label: "Solicitud de crédito firmada" }
        ],
        "Moral-SinCredito": [
          { key: "constancia_fiscal", label: "Constancia de situación fiscal (del mes en curso)" },
          { key: "comprobante_domicilio", label: "Comprobante de domicilio (más reciente)" },
          { key: "acta_constitutiva", label: "Acta constitutiva" },
          { key: "poder_notarial", label: "Poder notarial" },
          { key: "identificacion_apoderado", label: "Identificación del apoderado (vigente)" },
          { key: "opinion_positiva", label: "Opinión positiva (del mes en curso)" },
          { key: "propuesta_firmada", label: "Propuesta firmada" }
        ],
        "Fisica-Credito": [
          { key: "constancia_fiscal", label: "Constancia de situación fiscal (del mes en curso)" },
          { key: "comprobante_domicilio", label: "Comprobante de domicilio (más reciente)" },
          { key: "identificacion_apoderado", label: "Identificación del apoderado (vigente)" },
          { key: "opinion_positiva", label: "Opinión positiva (del mes en curso)" },
          { key: "propuesta_firmada", label: "Propuesta firmada" },
          { key: "solicitud_credito", label: "Solicitud de crédito firmada" }
        ],
        "Fisica-PrePago": [
          { key: "constancia_fiscal", label: "Constancia de situación fiscal (del mes en curso)" },
          { key: "comprobante_domicilio", label: "Comprobante de domicilio (más reciente)" },
          { key: "identificacion_apoderado", label: "Identificación del apoderado (vigente)" },
          { key: "opinion_positiva", label: "Opinión positiva (del mes en curso)" },
          { key: "propuesta_firmada", label: "Propuesta firmada" }
        ]
      },
      company: {
        personType: "Moral",
        creditType: "Credito",
        internalClientNumber: "",
        internalClientName: "",
        defaultWarehouseAddress: "",
        billingData: {
          name: "",
          rfc: "",
          phone: "",
          address: "",
          colony: "",
          postal_code: "",
          state_city: "",
          email: "",
        },
      },
      billingLabels: {
        name: "Nombre o razón social",
        rfc: "RFC",
        phone: "Teléfono",
        address: "Dirección",
        colony: "Colonia",
        postal_code: "Código postal",
        state_city: "Ciudad y estado",
        email: "Correo electrónico",
      },
      billingFields: [
        { key: "name", label: "Nombre o razón social" },
        { key: "rfc", label: "RFC", placeholder: "12 o 13 caracteres" },
        { key: "email", label: "Correo para facturas", type: "email" },
        { key: "phone", label: "Teléfono", type: "tel" },
        { key: "address", label: "Dirección fiscal" },
        { key: "colony", label: "Colonia" },
        { key: "postal_code", label: "Código postal" },
        { key: "state_city", label: "Ciudad y estado" },
      ],
      success: false,
      role: ''
    };
  },
  mounted() {
    this.role = localStorage.getItem('ew-user-role');
    this.fetchCompania();
  },
  computed: {
    currentRequiredDocuments() {
      const typeKey = `${this.company.personType || 'Moral'}-${this.company.creditType || 'Credito'}`;
      return this.documentLists[typeKey] || this.documentLists["Moral-Credito"];
    },
    docsLoaded() {
      return this.currentRequiredDocuments.filter(d => this.existingDocs[d.key] || this.selectedFiles[d.key]).length;
    }
  },
  methods: {
    async fetchCompania() {
      try {
        const res = await axios.get(`${commons.apiURL}/api/v1/compania/${this.userId}`);
        this.company = {
          ...this.company,
          ...res.data,
          personType: res.data.personType || "Moral",
          creditType: res.data.creditType || "Credito"
        };


        if (res.data.documents) {
          res.data.documents.forEach(doc => {
            // Si commons.apiURL es "http://localhost:3000", esto armará la URL correcta
            this.$set(this.existingDocs, doc.name, `${commons.apiURL}/api${doc.fileUrl}`);
          });
        }
      } catch (err) {
        console.error("Error al obtener compañía:", err);
      }
    },
    handleFileUpload(event, docKey) {
      // Usando asignación correcta para Vue 2 de archivos nativos
      if (event.target.files[0]) {
        this.$set(this.selectedFiles, docKey, event.target.files[0]);
      }
    },
    async updateCompania() {
      this.loading = true;
      this.success = false;
      try {
        const formData = new FormData();
        formData.append("company", JSON.stringify(this.company));

        Object.keys(this.selectedFiles).forEach(key => {
          formData.append(key, this.selectedFiles[key]);
        });

        await axios.put(`${commons.apiURL}/api/v1/compania/${this.userId}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        this.success = true;
        this.selectedFiles = {};
        this.fetchCompania();
        this.$bvToast.toast("Los datos de tu compañía se guardaron correctamente.", {
          title: "Cambios guardados", variant: "success", solid: true, autoHideDelay: 3500,
        });
      } catch (err) {
        console.error("Error al actualizar compañía:", err);
        this.$bvToast.toast("No pudimos guardar los cambios. Inténtalo de nuevo.", {
          title: "Error al guardar", variant: "danger", solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ew-docs {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid #eceef5;
  border-radius: 10px;
}

.ew-doc {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
}

.ew-doc + .ew-doc {
  border-top: 1px solid #eceef5;
}

.ew-doc-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f0f1f5;
  color: #6b7185;
}

.ew-doc-icon.is-done {
  background: #e7f4ec;
  color: #15803d;
}

.ew-doc-body {
  flex: 1 1 auto;
  min-width: 0;
}

.ew-doc-name {
  color: #1c2030;
  font-size: 0.875rem;
  font-weight: 600;
}

.ew-doc-meta {
  color: #6b7185;
  font-size: 0.78rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/Compania.vue